import { Component, Element, Prop, h, State } from '@stencil/core';
import { ZdsColors } from '@zig-design-system/design-tokens/dist/private/colors';
import Bind from 'bind-decorator';
import { ZdsTextColors } from '../../types';
import { generateCssClasses } from '../../utils/generate-css-classes';
import { createMutationObserver } from '../../utils/mutation-observer';
import { getSlottedElements } from '../../utils/slotted';

@Component({
	shadow: true,
	styleUrl: 'zds-badge.scss',
	tag: 'zds-badge',
})
export class ZdsBadge {
	dynamicRoundingElement: HTMLZdsDynamicRoundingElement;
	mutationObserver: MutationObserver;
	defaultSlotElement: HTMLSlotElement;
	overlayedSlotElement: HTMLSlotElement;

	@Prop({ reflect: true }) color: ZdsColors | ZdsTextColors = 'primary';
	@Prop() offsetX: number;
	@Prop() offsetY: number;
	@Prop() outlined: boolean = false;
	@Prop({ reflect: true }) pill: boolean;

	@State() hasBadgeContent: boolean;
	@State() hasOverlayedContent: boolean;
	@State() isOverlayedContentIcon: boolean;

	@Element() hostElement: HTMLZdsMapMarkerElement;

	connectedCallback() {
		this.mutationObserver = createMutationObserver(this.hostElement, this.handleMutation);
	}

	disconnectedCallback() {
		this.mutationObserver?.disconnect();
	}

	componentDidLoad() {
		this.updateContentVariables();
	}

	render() {
		return (
			<div class="zds-badge">
				<div class="zds-badge__overlayed-content">
					<slot name="overlayed" ref={(element: HTMLSlotElement) => (this.overlayedSlotElement = element)} />
				</div>
				{this.renderBadge()}
			</div>
		);
	}

	renderBadge() {
		const badgeClasses = generateCssClasses('zds-badge__badge', {
			[this.color]: !!this.color,
			empty: !this.hasBadgeContent,
			pill: this.pill,
			outlined: this.outlined,
			overlaying: this.hasOverlayedContent,
			overlayingIcon: this.isOverlayedContentIcon,
		});

		const badgeStyle = {
			marginBottom: this.hasOverlayedContent && this.offsetY ? this.offsetY + 'px' : undefined,
			marginLeft: this.hasOverlayedContent && this.offsetX ? this.offsetX + 'px' : undefined,
		};

		if (!this.hasBadgeContent) {
			return (
				<div class={badgeClasses} style={badgeStyle}>
					<slot ref={(element: HTMLSlotElement) => (this.defaultSlotElement = element)} />
				</div>
			);
		}

		return (
			<zds-dynamic-rounding
				ref={(element) => (this.dynamicRoundingElement = element)}
				containerClasses={badgeClasses}
				containerStyle={badgeStyle}
			>
				<slot ref={(element: HTMLSlotElement) => (this.defaultSlotElement = element)} />
			</zds-dynamic-rounding>
		);
	}

	@Bind
	handleMutation() {
		this.updateContentVariables();

		void this.dynamicRoundingElement?.update();
	}

	updateContentVariables() {
		if (!this.overlayedSlotElement || !this.defaultSlotElement) return;

		const filteredOverlayedNodes = getSlottedElements(this.overlayedSlotElement);
		this.hasBadgeContent = getSlottedElements(this.defaultSlotElement).length > 0;
		this.hasOverlayedContent = filteredOverlayedNodes.length > 0;
		this.isOverlayedContentIcon =
			filteredOverlayedNodes.length === 1 && filteredOverlayedNodes[0]?.nodeName === 'ZDS-ICON';
	}
}
